/**
 * User auth + subscription helpers.
 *
 * Two coexisting auth storage layouts share the same origin in prod:
 *   - Legacy app (Expo / lunarcrush.com): "lunar-UserSettings" — JSON
 *     blob with `{ signedIn, token }`.
 *   - New Next.js webapp (apps/app, DEV-10448): "lc-auth-token" — plain
 *     string token. Owned by apps/app/src/lib/auth.ts.
 *
 * Read both so the landing's signed-in branch lights up regardless of
 * which app the user authed through.
 */

import { buildSubscribeParams } from './stripe-subscribe-params';

const STORAGE_KEY = 'lunar-UserSettings';
const NEW_APP_TOKEN_KEY = 'lc-auth-token';
/** Placeholder tokens the new app may set during onboarding handoffs
 *  but that don't represent a real signed-in user. Mirror of the set in
 *  apps/app/src/lib/auth.ts so the landing doesn't false-positive
 *  when those keys are present. */
const NEW_APP_PLACEHOLDER_TOKENS = new Set([
  'dev-prototype-key',
  'onboarded-user',
  'oauth-authorized',
]);

/** Returns a real (non-placeholder) token from the new app's storage,
 *  or null. Centralised so callers don't have to know about the
 *  placeholder set. */
export function getNewAppToken(): string | null {
  try {
    const token = localStorage.getItem(NEW_APP_TOKEN_KEY);
    if (!token) return null;
    if (NEW_APP_PLACEHOLDER_TOKENS.has(token)) return null;
    return token;
  } catch {
    return null;
  }
}

/* ─── Types ──────────────────────────────────────────────────── */

export interface UserSettings {
  signedIn: boolean;
  token: string;
}

export interface SubscriptionDetail {
  name: string;            // "Discover", "Pro", "Enterprise", etc.
  subscription_id: string;
  ends: number;            // unix timestamp
  status: string;          // "active", etc.
  cancel_at?: number;      // unix timestamp if scheduled for cancellation
  from?: string;           // present if seat subscription (from another user)
  [key: string]: any;
}

export interface UserProfile {
  email?: string;
  level?: number;
  role?: string;
  referral_id?: string;
  subscriptions?: string;                // comma-separated e.g. "Discover,Pro" or ""
  subscription_detail?: SubscriptionDetail[];
  subscription_usage?: Record<string, any>;
  payments?: any[];
  [key: string]: any;
}

/* ─── Plan name mapping ──────────────────────────────────────── */

/**
 * Maps display plan names → Stripe subscription key prefixes.
 *
 * Display    → Stripe key
 * Individual → discover_*
 * Builder    → api_pro_*
 * Scale      → scale_*
 */
const PLAN_KEY_MAP: Record<string, string> = {
  individual: 'discover',
  discover: 'discover',
  builder: 'api_pro',
  api_pro: 'api_pro',
  scale: 'scale',
};

/** Maps billing cycle UI keys → Stripe interval suffixes */
const INTERVAL_MAP: Record<string, string> = {
  daily: 'daily',
  monthly: 'monthly',
  biannual: '6month',
  annual: 'yearly',
};

/**
 * Reverse map: subscription_detail.name → display plan name.
 * Used to match the user's current plan to the pricing card.
 */
const SUB_NAME_TO_PLAN: Record<string, string> = {
  discover: 'individual',
  pro: 'builder',
  scale: 'scale',
  enterprise: 'enterprise',
};

/* ─── Auth ────────────────────────────────────────────────────── */

/** Read the user's auth state from localStorage. Returns null if not signed in. */
export function getUserSettings(): UserSettings | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.signedIn && parsed?.token) return parsed as UserSettings;
    return null;
  } catch {
    return null;
  }
}

/** Check if user is currently signed in. Looks at both the legacy
 *  Expo settings blob AND the new Next.js webapp's plain token (DEV-10448),
 *  so a user authed through either path is recognised as signed-in. */
export function isSignedIn(): boolean {
  return getUserSettings() !== null || getNewAppToken() !== null;
}

/** Does the user hold a live session in the NEW webapp specifically?
 *  Unlike `isSignedIn()`, this ignores the legacy `lunar-UserSettings`
 *  blob — that blob persists for anyone who ever used the old Expo
 *  lunarcrush.com and is never cleared by the new app's logout, so the
 *  webapp (which only reads `lc-auth-token`) treats those users as
 *  signed-OUT. DEV-10812: the "/" → webapp redirect must use the
 *  webapp's own notion of a session, otherwise a returning user with a
 *  stale legacy blob gets bounced into the app and immediately shown the
 *  logged-out "Log In" shell — with no way back to the public homepage. */
export function hasWebappSession(): boolean {
  return getNewAppToken() !== null;
}

/** The bearer to fetch the profile with, read from whichever app the user
 *  signed in through — the legacy Expo blob OR the new webapp's plain token
 *  (DEV-10448). Returns null when neither holds a real token. Mirrors the
 *  dual-source check in `isSignedIn` so a user authed via the new app (the
 *  common path — Google/email both write `lc-auth-token`) is recognised as
 *  signed-in instead of being read as anonymous. DEV-10799: when only the
 *  new-app token was present, `useUserSubscription` read `getUserSettings()`
 *  alone, saw no legacy blob, and reported signed-out — which made the
 *  /pricing post-login auto-resume strip its `?subscribe=` param and drop
 *  the user back on the plain plan grid instead of the selected checkout. */
export function getActiveToken(): string | null {
  return getUserSettings()?.token ?? getNewAppToken();
}

/* ─── Profile / Subscription ─────────────────────────────────── */

/** API base uses the current origin so it works on localhost, staging, and prod */
const API_BASE = `${window.location.origin}/api4`;

/** Fetch the user's full profile including subscription info */
export async function fetchUserProfile(token: string): Promise<UserProfile | null> {
  try {
    const res = await fetch(`${API_BASE}/user/get-profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Stripe statuses that mean the subscription is dead — the user can start a
 * fresh checkout. Everything else (active, trialing, past_due, incomplete,
 * unpaid, or an empty/unknown status) is treated as live so we never let a
 * user with a recoverable subscription stack a duplicate one (DEV-11092).
 */
const DEAD_SUB_STATUSES = new Set(['canceled', 'cancelled', 'incomplete_expired']);

/** True when a subscription status counts as live (blocks a new purchase). */
export function isLiveSubscriptionStatus(status?: string): boolean {
  const s = (status ?? '').toLowerCase();
  if (!s) return true; // unknown/empty → treat as live (conservative)
  return !DEAD_SUB_STATUSES.has(s);
}

/**
 * Check if user has any live own subscription (excludes seat subs from other
 * users). A `past_due`/`incomplete`/`unpaid` sub still counts as live so the
 * user is routed to manage it instead of buying a second one; a fully
 * `canceled` sub does not, so re-subscribing stays allowed (DEV-11092).
 */
export function hasActiveSubscription(profile: UserProfile | null): boolean {
  if (!profile) return false;
  const details = profile.subscription_detail;
  if (details && details.length > 0) {
    return details.some((sub) => !sub.from && isLiveSubscriptionStatus(sub.status));
  }
  if (profile.subscriptions && profile.subscriptions.length > 0) return true;
  return false;
}

/**
 * Get the user's current plan display name (e.g. "individual", "builder", "scale"),
 * or null if no active subscription. Uses the highest-priority own subscription.
 */
export function getCurrentPlan(profile: UserProfile | null): string | null {
  if (!profile) return null;

  // Find first own subscription (not a seat sub)
  const ownSub = profile.subscription_detail?.find((sub) => !sub.from);
  if (ownSub?.name) {
    return SUB_NAME_TO_PLAN[ownSub.name.toLowerCase()] ?? ownSub.name.toLowerCase();
  }

  // Fall back to subscriptions string
  const subs = profile.subscriptions?.toLowerCase() ?? '';
  if (!subs) return null;
  for (const [subName, displayName] of Object.entries(SUB_NAME_TO_PLAN)) {
    if (subs.includes(subName)) return displayName;
  }

  return null;
}

/* ─── Stripe ─────────────────────────────────────────────────── */

/**
 * Build the Stripe subscription key from a display plan name + billing cycle.
 * e.g. ("Individual", "annual") → "discover_yearly"
 */
export function getSubscriptionKey(planName: string, billingCycle: string): string | null {
  const prefix = PLAN_KEY_MAP[planName.toLowerCase()];
  if (!prefix) return null;
  const interval = INTERVAL_MAP[billingCycle] ?? billingCycle;
  return `${prefix}_${interval}`;
}

/** Stripe publishable keys mirror apps/app/src/components/account/account-page.tsx
 *  so the inline checkout uses the same Stripe account/products as /account. */
const STRIPE_LIVE_PUBLISHABLE_KEY = 'pk_live_coOwlbQs72QyLeyhDErCxTYX';
const STRIPE_TEST_PUBLISHABLE_KEY = 'pk_test_dbT2inX1eNACtHgKPgicDQOJ';

export const STRIPE_PUBLISHABLE_KEY: string =
  ((import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.VITE_STRIPE_PUBLISHABLE_KEY) ?? STRIPE_LIVE_PUBLISHABLE_KEY;

/** Pick the publishable key that matches the env that minted the client secret.
 *  Stripe test secrets start with cs_test_ and require pk_test_ to initialise. */
export function stripePublishableKeyForClientSecret(clientSecret: string): string {
  if (clientSecret.startsWith('cs_test_')) return STRIPE_TEST_PUBLISHABLE_KEY;
  return STRIPE_PUBLISHABLE_KEY;
}

const API3_BASE = 'https://lunarcrush.com/api3';

export interface StripeEmbeddedSessionResponse {
  client_secret?: string;
  url?: string;
}

/** Why a checkout session couldn't be created. Surfaced to the caller so the
 *  UI can show a specific message and so failures are diagnosable in the
 *  console instead of collapsing into one generic "try again" (DEV-11117). */
export type EmbeddedSessionFailureReason =
  | 'no-plan' // unknown plan key — config bug, not transient
  | 'no-token' // user not signed in, or a post-signup placeholder token still in storage
  | 'unauthorized' // api3 401/403 — a freshly minted token isn't a valid session yet
  | 'rate-limited' // api3 429 — checkout creation is capped at 4/min/IP
  | 'server-error' // api3 5xx
  | 'http-error' // other non-ok response
  | 'no-secret' // 200 OK but no client_secret in the payload
  | 'network'; // fetch threw (offline, blocked, DNS)

export type EmbeddedSessionResult =
  | { ok: true; client_secret: string; url?: string }
  | { ok: false; reason: EmbeddedSessionFailureReason; status?: number; detail?: string };

/** Failures worth retrying. The defining DEV-11117 case is "new account
 *  signup": the post-signup handoff can leave a placeholder token (`no-token`)
 *  or a not-yet-validated real token (`unauthorized`) in storage for a beat, so
 *  a short backoff lets the real session settle before the user sees an error.
 *  `no-plan`/`no-secret`/`http-error` are deterministic, so we fail fast. */
const RETRYABLE_SUBSCRIBE_REASONS = new Set<EmbeddedSessionFailureReason>([
  'no-token',
  'unauthorized',
  'rate-limited',
  'server-error',
  'network',
]);

/** Backoff between retries, in ms. Two retries (~2.4s total) comfortably
 *  covers the token-handoff race without making a real failure feel stuck. */
const SUBSCRIBE_RETRY_DELAYS_MS = [800, 1600];

interface EmbeddedSubscribeOptions {
  planName: string;
  billingCycle: string;
  redirect?: string;
  theme?: 'dark' | 'light';
  referral?: string;
}

/** Return the bearer token from whichever auth storage the user signed in
 *  through (legacy Expo settings blob or new app's plain token). */
export function getAuthToken(): string | null {
  const settings = getUserSettings();
  if (settings?.token) return settings.token;
  return getNewAppToken();
}

/** One attempt at creating the embedded checkout session. Reads the token
 *  fresh on every call so a retry can pick up a real token that has since
 *  replaced a post-signup placeholder. */
async function attemptEmbeddedSubscribeSession(
  options: EmbeddedSubscribeOptions,
): Promise<EmbeddedSessionResult> {
  const subscription = getSubscriptionKey(options.planName, options.billingCycle);
  if (!subscription) return { ok: false, reason: 'no-plan' };
  const token = getAuthToken();
  if (!token) return { ok: false, reason: 'no-token' };
  // DEV-12161: build the query through buildSubscribeParams so the emitted key
  // set is the single, test-guarded choke point. api3's stripe/subscribe
  // hard-400s any key it does not allowlist (see STRIPE_SUBSCRIBE_ALLOWED_PARAMS),
  // and a 400 here means nobody can pay. `theme` is dropped and the affiliate id
  // is mapped to `promotekit_referral` inside the builder.
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(
    buildSubscribeParams({
      subscription,
      mode: 'embedded_page',
      redirect: options.redirect,
      theme: options.theme,
      referral: options.referral,
    }),
  )) {
    if (value != null && value !== '') params.set(key, value);
  }
  try {
    const res = await fetch(`${API3_BASE}/stripe/subscribe?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      // Never reuse a cached checkout session — each one is single-use and
      // tied to the live token.
      cache: 'no-store',
    });
    if (!res.ok) {
      const reason: EmbeddedSessionFailureReason =
        res.status === 401 || res.status === 403
          ? 'unauthorized'
          : res.status === 429
            ? 'rate-limited'
            : res.status >= 500
              ? 'server-error'
              : 'http-error';
      // Capture api3's error body. It carries the actual reason (e.g.
      // "Invalid input parameter: theme") that DEV-11117 was blind to because
      // the body was previously discarded — keep it for the failure log.
      let detail: string | undefined;
      try {
        detail = (await res.text())?.slice(0, 300) || undefined;
      } catch {
        detail = undefined;
      }
      return { ok: false, reason, status: res.status, detail };
    }
    const data = (await res.json()) as StripeEmbeddedSessionResponse;
    if (!data?.client_secret) return { ok: false, reason: 'no-secret', status: res.status };
    return { ok: true, client_secret: data.client_secret, url: data.url };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/** Start an embedded Stripe Checkout session (DEV-10504). Mirrors apps/app's
 *  createStripeSubscribeSession with `mode=embedded_page` so /pricing can
 *  mount the same inline checkout already used by /account.
 *
 *  DEV-11117: retries transient failures with a short backoff (the post-signup
 *  token-handoff race) and returns a discriminated result that names the
 *  failure, so the embed can show a useful message and the cause lands in the
 *  console instead of a silent null. */
export async function createEmbeddedSubscribeSession(
  options: EmbeddedSubscribeOptions,
): Promise<EmbeddedSessionResult> {
  let result = await attemptEmbeddedSubscribeSession(options);
  for (
    let attempt = 0;
    attempt < SUBSCRIBE_RETRY_DELAYS_MS.length &&
    !result.ok &&
    RETRYABLE_SUBSCRIBE_REASONS.has(result.reason);
    attempt++
  ) {
    const delay = SUBSCRIBE_RETRY_DELAYS_MS[attempt];
    const status = result.ok ? '' : result.status ? ` ${result.status}` : '';
    console.warn(
      `[Pricing] checkout session attempt ${attempt + 1} failed (${
        (result as { reason: EmbeddedSessionFailureReason }).reason
      }${status}); retrying in ${delay}ms`,
    );
    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await attemptEmbeddedSubscribeSession(options);
  }
  if (!result.ok) {
    console.warn(
      `[Pricing] createEmbeddedSubscribeSession failed: reason=${result.reason}` +
        `${result.status ? ` status=${result.status}` : ''} hadToken=${!!getAuthToken()}` +
        `${result.detail ? ` detail=${result.detail}` : ''}`,
    );
  }
  return result;
}

/* ─── Plan hierarchy ─────────────────────────────────────────── */

/** Tier ordering used to label CTAs as Upgrade vs Downgrade. Higher
 *  number = higher tier. Includes both the display name (`individual`,
 *  `builder`) and the backend slug (`discover`, `api_pro`) so callers
 *  can pass whichever shape they have on hand. */
const PLAN_TIER: Record<string, number> = {
  hobby: 0,
  individual: 1,
  discover: 1,
  builder: 2,
  api_pro: 2,
  pro: 2,
  scale: 3,
  enterprise: 4,
};

export type PlanRelation = 'current' | 'upgrade' | 'downgrade' | 'unknown';

/** Compare the user's current plan against a target plan card. Returns
 *  `current` when they match, `upgrade`/`downgrade` based on tier, or
 *  `unknown` when either side is outside the known hierarchy. */
export function comparePlanTier(current: string | null, target: string): PlanRelation {
  if (!current) return 'unknown';
  const c = PLAN_TIER[current.toLowerCase()];
  const t = PLAN_TIER[target.toLowerCase()];
  if (c === undefined || t === undefined) return 'unknown';
  if (c === t) return 'current';
  return t > c ? 'upgrade' : 'downgrade';
}
